"use client";

import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { type Comedian } from "@punchup/data";
import { type Podcast } from "@punchup/data/models/podcasts";
import { type SpecialRow } from "@/data/specials";
import { getCDNImageURL } from "@/data/images";
import { rgbDataURL } from "@punchup/utils/rgbDataURL";
import { formatClapCount, formatSpecialAge } from "@/utils/formatters";
import { useSpecialReactions } from "@/hooks/specials";
import {
  CTA_SOURCES,
  PodcastEpisodeEvents,
  UserEvents,
  PodcastEvents,
  trackEvent,
} from "@punchup/analytics";
import ComedianVideoThumbnail from "./ComedianVideoThumbnail";
import { ChevronRight, Headphones } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

interface FeaturedPodcastProps {
  comedian: Comedian;
  podcast: Podcast;
  latestEpisode: SpecialRow;
}

export default function FeaturedPodcast({
  comedian,
  podcast,
  latestEpisode,
}: FeaturedPodcastProps) {
  const searchParams = useSearchParams();
  const { count: clapCount } = useSpecialReactions({
    specialId: latestEpisode.id,
  });

  const podcastHref = `/${comedian.slug}/podcasts/${podcast.slug}`;
  const episodeHref = `/${podcast.owner_comedian_slug || comedian.slug}/${latestEpisode.slug}?podcast_id=${podcast.id}&collection_id=${podcast.collection_id}`;

  useEffect(() => {
    const urlParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      urlParams[key] = value;
    });
    trackEvent(UserEvents.LOADED_CONTENT_PAGE, {
      ComedianId: comedian.id,
      Comedian: comedian.display_name,
      PodcastId: podcast.id,
      PodcastName: podcast.name,
      SpecialId: latestEpisode.id,
      "Special Title": latestEpisode.title,
      url_params: urlParams,
    });
  }, [
    comedian.id,
    comedian.display_name,
    podcast.id,
    podcast.name,
    latestEpisode.id,
    latestEpisode.title,
    searchParams,
  ]);

  const ctaShownRef = useRef(false);
  useEffect(() => {
    if (ctaShownRef.current) return;
    ctaShownRef.current = true;
    trackEvent(PodcastEpisodeEvents.CTA_SHOWN, {
      cta_source: CTA_SOURCES.COMEDIAN_PAGE_FEATURED_PODCAST,
      special_id: latestEpisode.id,
      special_slug: latestEpisode.slug,
      podcast_id: podcast.id,
      collection_id: podcast.collection_id,
      comedian_slug: comedian.slug,
    });
  }, [
    latestEpisode.id,
    latestEpisode.slug,
    podcast.id,
    podcast.collection_id,
    comedian.slug,
  ]);

  const handlePodcastClick = () => {
    trackEvent(PodcastEvents.PODCAST_CLICKED, {
      podcast_id: podcast.id,
      collection_id: podcast.collection_id,
      comedian_id: comedian.id,
    });
  };

  const handleEpisodeClick = () => {
    trackEvent(UserEvents.CLICKED_CONTENT_BANNER, {
      ComedianId: comedian.id,
      Comedian: comedian.display_name,
      SpecialId: latestEpisode.id,
      "Special Title": latestEpisode.title,
      PodcastId: podcast.id,
    });
    trackEvent(UserEvents.CLICKED_WATCH_FEATURED_SPECIAL, {
      CTAButtonText: "featured-podcast-card",
      ComedianId: comedian.id,
      SpecialId: latestEpisode.id,
      SpecialPrice: latestEpisode.price,
      PodcastId: podcast.id,
    });
  };

  const posterUrl = podcast.poster_image_id
    ? getCDNImageURL(
        podcast.poster_image_id,
        "gravity=auto,fit=cover,width=400,height=400"
      )
    : null;

  return (
    <div className="bg-[#F2F2F3] rounded-xl p-3 pb-4 md:p-6">
      {/* Podcast header */}
      <Link
        href={podcastHref}
        onClick={handlePodcastClick}
        className="flex items-center gap-3 mb-3 md:mb-6"
      >
        {posterUrl ? (
          <div className="relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden">
            <Image
              src={posterUrl}
              alt={podcast.name}
              fill
              placeholder="blur"
              blurDataURL={rgbDataURL(16, 16, 16)}
              className="object-cover"
            />
          </div>
        ) : (
          <div className="w-14 h-14 flex-shrink-0 rounded-lg bg-gray-300 flex items-center justify-center">
            <Headphones className="w-6 h-6 text-gray-500" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-0.5">
            <span className="text-[10px] font-semibold text-primary uppercase tracking-wide">
              Podcast
            </span>
          </div>
          <h2 className="text-primary-blue font-bold text-base leading-tight line-clamp-1">
            {podcast.name}
          </h2>
        </div>

        <span className="hidden md:flex items-center gap-1 text-xs font-medium text-secondary-blue flex-shrink-0">
          All Episodes
          <ChevronRight className="w-5 h-5" />
        </span>
        <ChevronRight className="w-5 h-5 text-secondary-blue flex-shrink-0 md:hidden" />
      </Link>

      {/* Featured episode — stacked on mobile, side-by-side on desktop */}
      <Link
        href={episodeHref}
        onClick={handleEpisodeClick}
        className="flex flex-col gap-y-2 md:flex-row md:gap-x-[30px] md:items-center"
      >
        <ComedianVideoThumbnail
          special={latestEpisode}
          className="w-full md:w-[unset] md:h-[160px] lg:h-[225px]"
          borderRadius={12}
          withPlayIcon
        />
        <div className="flex flex-col justify-center gap-y-1 px-0.5 md:flex-1 md:pr-20">
          <h3 className="text-primary-blue font-semibold text-base md:text-[22px] leading-snug line-clamp-2 md:line-clamp-none">
            {latestEpisode.title}
          </h3>
          <div className="flex flex-row flex-wrap items-center gap-x-2 text-sm text-secondary-blue">
            <span>{formatClapCount(clapCount)} claps</span>
            {latestEpisode.published_at && (
              <>
                <div className="w-1 h-1 rounded-full bg-secondary-blue" />
                <span>{formatSpecialAge(latestEpisode.published_at)}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
